import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';

export interface UserProfile {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  attributes?: Record<string, any>;
  roles: string[];
  organizationId?: string;
  organizationName?: string;
}

// Global cache for user profile to prevent duplicate API calls
const profileCache: {
  data: UserProfile | null;
  loading: boolean;
  promise: Promise<UserProfile> | null;
  lastFetch: number;
} = {
  data: null,
  loading: false,
  promise: null,
  lastFetch: 0
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export const useUserProfile = () => {
  const { keycloak } = useKeycloak();
  const [userProfile, setUserProfile] = useState<UserProfile | undefined>(profileCache.data || undefined);

  useEffect(() => {
    if (!keycloak.authenticated) {
      setUserProfile(undefined);
      return;
    }

    const now = Date.now();
    const isCacheValid = profileCache.data && (now - profileCache.lastFetch) < CACHE_DURATION;

    // If we have valid cached data, use it immediately
    if (isCacheValid) {
      setUserProfile(profileCache.data!);
      return;
    }

    // If there's already a request in progress, wait for it
    if (profileCache.loading && profileCache.promise) {
      profileCache.promise
        .then(data => setUserProfile(data))
        .catch(error => console.error('Failed to load user profile:', error));
      return;
    }
    
    const loadProfile = async (): Promise<UserProfile> => {
      try {
        console.log('[USER_PROFILE] Loading profile data...');
        const info = await keycloak.loadUserProfile();
        const currentOrgId = localStorage.getItem('currentOrganizationId');
        
        // Get organization info from token if available
        const tokenParsed = keycloak.tokenParsed || {};
        const organizationId = currentOrgId || tokenParsed.organization_id;
        
        // Look for organization name in token claims if available
        const organizationName = tokenParsed.organization_name || 
          (organizationId ? 'Organization ' + organizationId.substring(0, 8) : undefined);
        
        // Extract roles - first check if roles are directly in the token
        let roles: string[] = [];
        
        if (tokenParsed.roles && Array.isArray(tokenParsed.roles)) {
          // Get roles directly from the token
          roles = tokenParsed.roles;
          console.log('Using roles directly from token:', roles);
        }
        // Then fallback to organizations if available
        else if (tokenParsed.organizations && organizationId && tokenParsed.organizations[organizationId]) {
          // Get roles from the current organization
          roles = tokenParsed.organizations[organizationId].roles || [];
          console.log(`Using roles from organizations[${organizationId}]:`, roles);
        }
        
        const profile: UserProfile = {
          ...info,
          id: tokenParsed.sub,
          roles,
          organizationId,
          organizationName
        };

        // Update cache
        profileCache.data = profile;
        profileCache.lastFetch = Date.now();
        profileCache.loading = false;
        profileCache.promise = null;

        console.log('[USER_PROFILE] Profile loaded and cached');
        return profile;
      } catch (error) {
        profileCache.loading = false;
        profileCache.promise = null;
        console.error('Failed to load user profile:', error);
        throw error;
      }
    };

    // Set loading state and cache the promise
    profileCache.loading = true;
    profileCache.promise = loadProfile();

    profileCache.promise
      .then(data => setUserProfile(data))
      .catch(error => console.error('Failed to load user profile:', error));
  }, [keycloak.authenticated]);

  return userProfile;
};

// Helper function to clear the cache (useful for logout)
export const clearUserProfileCache = () => {
  profileCache.data = null;
  profileCache.loading = false;
  profileCache.promise = null;
  profileCache.lastFetch = 0;
  console.log('[USER_PROFILE] Cache cleared');
};
